import { ReactNode, useCallback } from "react";
import { NavLink } from "react-router-dom";

import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap } from "@mui/material/SvgIcon";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { SxProps, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import snakeCase from "lodash/snakeCase";

import useIsMobile from "hooks/use-is-mobile.hook";

import LegacySideMenuItemContent from "./legacy-side-menu-item-content.component";

type LegacySideMenuItemProps = {
  isDefaultPath?: boolean;
  to: string;
  primary: string;
  Icon: OverridableComponent<SvgIconTypeMap<{}, "svg">> & {
    muiName: string;
  };
  divider?: boolean;
  isFallbackPath?: boolean;
  badgeContent?: ReactNode;
  tooltip?: string;
  sx?: SxProps;
  isDotWarning?: boolean;
  allowWrap?: boolean;
};

export default function LegacySideMenuItem({
  isDefaultPath = false,
  to,
  primary,
  Icon,
  divider,
  isFallbackPath = false,
  badgeContent,
  tooltip,
  sx,
  isDotWarning,
  allowWrap = false
}: LegacySideMenuItemProps) {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isSmallScreen = useMediaQuery("(max-height:1079px)");

  const getSubMenuItemSx = useCallback(
    (isActive: boolean): any => {
      return {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        borderRadius: "8px",
        px: 1.25,
        py: isMobile || isSmallScreen ? 1.6 : 1.85,
        ml: primary === "QuickBooks" ? "-3px" : 0,
        position: "relative",
        "&::before": {
          content: "''",
          display:
            isActive || (isFallbackPath && isDefaultPath) ? "block" : "none",
          width: "calc(100% - 8px)",
          height: "calc(100% - 8px)",
          position: "absolute",
          left: "4px",
          top: "4px",
          border: "3px solid #fff",
          boxSizing: "border-box",
          borderRadius: "17px"
        },
        ...sx
      };
    },
    [isMobile, isSmallScreen, primary, isFallbackPath, isDefaultPath, sx]
  );

  return (
    <Tooltip
      title={<Typography variant="body1">{tooltip}</Typography>}
      placement="right"
      disableInteractive
      open={isMobile ? false : undefined}
    >
      <ListItem
        divider={divider}
        disablePadding
        data-testid={snakeCase(primary)}
        sx={{
          borderColor: theme.palette.neutral[500],
          mb: isMobile || isSmallScreen ? -1 : undefined
        }}
      >
        <NavLink
          to={to}
          style={{ display: "flex", flex: 1, textDecoration: "none" }}
        >
          {({ isActive }) => (
            <Box sx={getSubMenuItemSx(isActive)}>
              <LegacySideMenuItemContent
                isActive={isActive}
                primary={primary}
                Icon={Icon}
                badgeContent={badgeContent}
                isDotWarning={isDotWarning}
                allowWrap={allowWrap}
              />
            </Box>
          )}
        </NavLink>
      </ListItem>
    </Tooltip>
  );
}
